import React from 'react';
import { Box, Typography } from '@mui/material';
import OntologyBrowser, {
    CodeText, OntologyColumn, ScopeLevelChip, SCOPE_LEVEL_FILTER, StatusChip, STATUS_FILTER,
} from './OntologyBrowser';
import {
    SOURCE_TYPE_LABEL, formatValue, optionsFromLabels, sourceTypeLabel,
} from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/**
 * Where a value came from. `incumbent_config` is the one that matters most: those are
 * thresholds lifted from the running configuration — what the system IS doing, which
 * nobody has yet confirmed is what the institution INTENDS. They arrive as drafts with
 * no approver and no effective date.
 */
const SOURCE_TYPE_FILTER = {
    key: 'source_type',
    label: 'Source',
    options: optionsFromLabels(SOURCE_TYPE_LABEL, [
        'incumbent_config', 'policy', 'calibrated', 'inferred', 'default',
    ], 'All sources'),
};

const columns: OntologyColumn[] = [
    {
        key: 'parameter',
        label: 'Parameter',
        width: '32%',
        render: (doc) => (
            <Box>
                <Typography component="div" sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 500 }}>
                    {doc.parameter_code}
                </Typography>
                {/* A parameter binds by conceptCode, NOT by its own name — showing the
                    concept alongside is what makes a mis-bound parameter visible. */}
                <CodeText>{doc.concept_code || 'no concept — governs nothing'}</CodeText>
            </Box>
        ),
    },
    {
        key: 'value',
        label: 'Value',
        render: (doc) => (
            <Typography component="span" sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 500 }}>
                {formatValue(doc.value, doc.unit)}
            </Typography>
        ),
    },
    {
        key: 'policy_code',
        label: 'Policy',
        width: '20%',
        render: (doc) => <CodeText>{doc.policy_code || '—'}</CodeText>,
    },
    {
        key: 'source_type',
        label: 'Source',
        // The same label the Source filter shows.
        render: (doc) => (
            <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                {sourceTypeLabel(doc.source_type)}
            </Typography>
        ),
    },
    {
        key: 'scope_level',
        label: 'Scope',
        render: (doc) => <ScopeLevelChip scopeLevel={doc.scope_level} />,
    },
    {
        key: 'status',
        label: 'Status',
        render: (doc) => <StatusChip status={doc.status} />,
    },
];

/**
 * The three ways anyone actually arrives at this tab.
 *
 * A threshold has no meaning standing alone — "2 · count · draft" tells nobody anything —
 * so this is a WORKLIST, not a catalogue. What people come here to ask is "what is waiting
 * on me?" and "what are we currently running?", and those are two saved filters, not a
 * dropdown someone has to assemble.
 *
 * `scope_level: organisation` on the first view is load-bearing: a platform-tier draft is
 * not this institution's to approve, so listing it under "Awaiting approval" would be a
 * queue of work nobody can do.
 */
const VIEWS = [
    {
        id: 'pending',
        label: 'Awaiting approval',
        filters: { status: 'draft', scope_level: 'organisation' },
        empty: 'Nothing is waiting for approval.',
    },
    {
        id: 'in_force',
        label: 'In force',
        filters: { status: 'approved', scope_level: '' },
        empty: 'No approved thresholds match these filters.',
    },
    {
        id: 'all',
        label: 'All thresholds',
        filters: { status: '', scope_level: '' },
        empty: 'No thresholds match these filters.',
    },
] as const;

/** Which view the current query string corresponds to; 'all' when it matches neither. */
const activeViewOf = (filters: Record<string, string>) =>
    VIEWS.find((view) => view.id !== 'all'
        && view.filters.status === (filters.status ?? '')
        && view.filters.scope_level === (filters.scope_level ?? '')) ?? VIEWS[2];

interface ParametersBrowserProps {
    filters: Record<string, string>;
    setFilters: (patch: Record<string, string>) => void;
    onRowSelect?: (doc: Record<string, any>) => void;
    selectedCode?: string | null;
}

const ParametersBrowser: React.FC<ParametersBrowserProps> = ({
    filters, setFilters, onRowSelect, selectedCode,
}) => {
    // Set when the agents drawer links here for one activity template.
    const activity = filters.activity;
    const activeView = activeViewOf(filters);

    const toolbar = (
        <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5, mb: activity ? 1.5 : 0 }}>
                {VIEWS.map((view) => {
                    const isActive = view.id === activeView.id;
                    return (
                        <Box
                            key={view.id}
                            component="button"
                            type="button"
                            aria-current={isActive ? 'true' : undefined}
                            // Both keys in ONE update — switching from a view that set
                            // scope_level to one that does not must CLEAR it, and two
                            // sequential single-key writes would drop the first.
                            onClick={() => setFilters({
                                status: view.filters.status,
                                scope_level: view.filters.scope_level,
                            })}
                            sx={{
                                px: 1.75, py: 0.75,
                                border: '1px solid',
                                borderColor: isActive ? dsVar('accent-fg') : dsVar('line'),
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '0.8125rem',
                                fontWeight: isActive ? 600 : 500,
                                color: isActive ? dsVar('accent-fg') : dsVar('fg-2'),
                                backgroundColor: isActive ? 'rgba(255,222,89,0.08)' : 'transparent',
                                '&:hover': { borderColor: isActive ? dsVar('accent-fg') : dsVar('line') },
                            }}
                        >
                            {view.label}
                        </Box>
                    );
                })}
            </Box>

            {activity && (
                <Typography
                    component="p"
                    sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}
                >
                    Showing thresholds that configure{' '}
                    <Typography component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: dsVar('accent-fg'), fontSize: '0.8125rem' }}>
                        {activity}
                    </Typography>
                    {' '}— those binding to something in its configuration, by concept, config
                    path or parameter code.
                </Typography>
            )}
        </Box>
    );

    return (
        <OntologyBrowser
            kind="parameters"
            filters={filters}
            columns={columns}
            filterControls={[SOURCE_TYPE_FILTER, SCOPE_LEVEL_FILTER, STATUS_FILTER]}
            searchPlaceholder="Search threshold, concept or policy codes"
            emptyMessage={activeView.empty}
            toolbar={toolbar}
            onRowSelect={onRowSelect}
            selectedCode={selectedCode}
        />
    );
};

export default ParametersBrowser;
